import { UtilS } from '@modules/Util/S/Function/module/UtilS.js';
import { UtilLoggerS } from '@modules/Util/Logger/S/Function/module/UtilLoggerS';

type TimerColor = 'yellow' | 'orange' | 'green' | 'blue';

interface ColorMapping {
  bg: string;
  fg: string;
}

interface TimerEntry {
  startTime: number;
  name: string;
}

export class UtilTimer {
  // eslint-disable-next-line no-use-before-define
  private static instance: UtilTimer;
  private readonly timers: Map<string, TimerEntry> = new Map();

  private readonly colorMap: Record<TimerColor, ColorMapping> = {
    yellow: { bg: 'bg_yellow', fg: 'black' },
    orange: { bg: 'bg_red', fg: 'black' }, // ANSI에 orange 없음, red로 대체
    green: { bg: 'bg_green', fg: 'black' },
    blue: { bg: 'bg_blue', fg: 'black' },
  };

  private readonly defaultColor: TimerColor = 'yellow';

  private constructor() {}

  /**
   * Get singleton instance of UtilTimer
   * @returns UtilTimer instance
   */
  static getInstance(): UtilTimer {
    if (!UtilTimer.instance) {
      UtilTimer.instance = new UtilTimer();
    }
    return UtilTimer.instance;
  }

  /**
   * Get colors for timer display
   * @param color - Color name for timer display
   * @returns Color mapping object
   */
  private getColors(color?: TimerColor): ColorMapping {
    return this.colorMap[color || this.defaultColor];
  }

  /**
   * Check if running in production environment or test mode
   * @returns True if production environment or Jest test mode
   */
  private isProductionOrTest(): boolean {
    try {
      // Check if in Jest test environment
      if (
        typeof jest !== 'undefined' ||
        (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') ||
        (typeof process !== 'undefined' && process.env?.JEST_WORKER_ID !== undefined)
      ) {
        return true;
      }

      // Check production environment
      return UtilS.isProd();
    } catch {
      // Fallback for environments where UtilS is not available
      if (typeof process !== 'undefined' && process.env) {
        return process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test';
      }
      return false;
    }
  }

  /**
   * Get current timestamp in milliseconds
   * @returns Current timestamp
   */
  private getCurrentTime(): number {
    return Date.now();
  }

  /**
   * Start a timer with given name
   * @param name - Unique timer name
   */
  start(name: string): void {
    if (this.isProductionOrTest()) return;

    const startTime = this.getCurrentTime();
    this.timers.set(name, { startTime, name });
  }

  /**
   * End a timer and log the elapsed time
   * @param name - Timer name to end
   * @param color - Color for log display
   */
  end(name: string, color?: TimerColor): void {
    if (this.isProductionOrTest()) return;

    const timerEntry = this.timers.get(name);
    if (!timerEntry) {
      console.warn(`Timer "${name}" not found`);
      return;
    }

    const elapsed = this.getCurrentTime() - timerEntry.startTime;
    const { bg, fg } = this.getColors(color);

    try {
      UtilLoggerS.colorLog([` ${name}: ${elapsed}ms `], [fg], [bg]);
    } catch {
      // Fallback for environments where UtilLoggerS is not available
      console.log(`[TIMER] ${name}: ${elapsed}ms`);
    }

    this.timers.delete(name);
  }

  /**
   * Get elapsed time for a running timer without ending it
   * @param name - Timer name
   * @returns Elapsed time in milliseconds, or null if timer not found
   */
  getElapsed(name: string): number | null {
    const timerEntry = this.timers.get(name);
    if (!timerEntry) return null;

    return this.getCurrentTime() - timerEntry.startTime;
  }

  /**
   * Check if timer is running
   * @param name - Timer name
   * @returns True if timer is running
   */
  isRunning(name: string): boolean {
    return this.timers.has(name);
  }

  /**
   * Clear all running timers
   */
  clearAll(): void {
    this.timers.clear();
  }

  /**
   * Get list of all running timer names
   * @returns Array of timer names
   */
  getRunningTimers(): string[] {
    return Array.from(this.timers.keys());
  }

  /**
   * Static method for easy access - start timer
   * @param name - Timer name
   */
  static start(name: string): void {
    UtilTimer.getInstance().start(name);
  }

  /**
   * Static method for easy access - end timer
   * @param name - Timer name
   * @param color - Color for log display
   */
  static end(name: string, color?: TimerColor): void {
    UtilTimer.getInstance().end(name, color);
  }
}
